'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, MessageSquare, Zap, CheckCircle, ShoppingBag, Check, Megaphone, FileText, Settings } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useWalletAuth } from '@/contexts/WalletAuthContext';

interface Notification {
  id: string;
  notification_type: string;
  title: string;
  message: string;
  is_read: boolean;
  metadata: Record<string, any>;
  created_at: string;
}

const typeConfig: Record<string, { icon: any; color: string; bg: string; border: string; label: string }> = {
  chat_reply: {
    icon: MessageSquare,
    color: 'text-[#16C2FF]',
    bg: 'bg-[rgba(22,194,255,0.1)]',
    border: 'border-[rgba(22,194,255,0.2)]',
    label: 'Chat Reply',
  },
  airdrop_status: {
    icon: Zap,
    color: 'text-[#F59E0B]',
    bg: 'bg-[rgba(245,158,11,0.1)]',
    border: 'border-[rgba(245,158,11,0.2)]',
    label: 'Airdrop Update',
  },
  claim_confirmation: {
    icon: CheckCircle,
    color: 'text-[#10B981]',
    bg: 'bg-[rgba(16,185,129,0.1)]',
    border: 'border-[rgba(16,185,129,0.2)]',
    label: 'Claim Update',
  },
  package_receipt: {
    icon: ShoppingBag,
    color: 'text-[#A78BFA]',
    bg: 'bg-[rgba(167,139,250,0.1)]',
    border: 'border-[rgba(167,139,250,0.2)]',
    label: 'Package Receipt',
  },
  project_submission: {
    icon: FileText,
    color: 'text-[#F59E0B]',
    bg: 'bg-[rgba(245,158,11,0.1)]',
    border: 'border-[rgba(245,158,11,0.2)]',
    label: 'Project Update',
  },
  admin_broadcast: {
    icon: Megaphone,
    color: 'text-[#16C2FF]',
    bg: 'bg-[rgba(22,194,255,0.1)]',
    border: 'border-[rgba(22,194,255,0.2)]',
    label: 'Announcement',
  },
  project_update: {
    icon: Settings,
    color: 'text-[#8B5CF6]',
    bg: 'bg-[rgba(139,92,246,0.1)]',
    border: 'border-[rgba(139,92,246,0.2)]',
    label: 'Project Update',
  },
  system: {
    icon: Settings,
    color: 'text-[#93A4B8]',
    bg: 'bg-[rgba(147,164,184,0.1)]',
    border: 'border-[rgba(147,164,184,0.2)]',
    label: 'System',
  },
};

const defaultConfig = {
  icon: Bell,
  color: 'text-[#93A4B8]',
  bg: 'bg-[rgba(147,164,184,0.1)]',
  border: 'border-[rgba(147,164,184,0.2)]',
  label: 'Notification',
};

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function NotificationPanel() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const reminderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { walletUser, supabaseUser } = useWalletAuth();
  const supabase = createClient();

  // Use supabaseUser id as primary, fallback to walletUser id
  const userId = supabaseUser?.id || walletUser?.id;

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (!error && data) setNotifications(data as Notification[]);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchNotifications();

    // Real-time subscription for new notifications
    const channel = supabase
      .channel(`notifications_user_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev]);
          // Show reminder pulse when new notification arrives and panel is closed
          if (!open) {
            setShowReminder(true);
            if (reminderTimerRef.current) clearTimeout(reminderTimerRef.current);
            reminderTimerRef.current = setTimeout(() => setShowReminder(false), 8000);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (reminderTimerRef.current) clearTimeout(reminderTimerRef.current);
    };
  }, [userId, fetchNotifications]);

  // Show reminder pulse on mount if there are unread notifications
  useEffect(() => {
    if (unreadCount > 0 && !open) {
      setShowReminder(true);
      reminderTimerRef.current = setTimeout(() => setShowReminder(false), 6000);
    }
    return () => {
      if (reminderTimerRef.current) clearTimeout(reminderTimerRef.current);
    };
  }, [unreadCount]);

  // Hide reminder when panel opens
  useEffect(() => {
    if (open) setShowReminder(false);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const markAllRead = async () => {
    if (!userId) return;
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .in('id', unreadIds);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const markOneRead = async (id: string) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
  };

  if (!userId) return null;

  return (
    <div className="relative" ref={panelRef}>
      {/* Reminder tooltip — appears above bell when there are unread messages */}
      {showReminder && unreadCount > 0 && !open && (
        <div className="absolute -top-10 right-0 z-50 pointer-events-none">
          <div className="bg-[#0D1B2A] border border-[rgba(22,194,255,0.3)] rounded-lg px-2.5 py-1.5 shadow-lg flex items-center gap-1.5 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D4D] animate-pulse flex-shrink-0" />
            <span className="text-[10px] text-[#E6F1FF] font-medium">
              {unreadCount} unread message{unreadCount > 1 ? 's' : ''}
            </span>
          </div>
          {/* Arrow */}
          <div className="absolute -bottom-1.5 right-3 w-3 h-3 bg-[#0D1B2A] border-r border-b border-[rgba(22,194,255,0.3)] rotate-45" />
        </div>
      )}

      {/* Bell Button */}
      <button
        onClick={() => { setOpen((v) => !v); if (!open) fetchNotifications(); }}
        className={`relative flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 ${
          unreadCount > 0
            ? 'text-[#16C2FF] bg-[rgba(22,194,255,0.1)] hover:bg-[rgba(22,194,255,0.15)]'
            : 'text-[#93A4B8] hover:text-[#E6F1FF] hover:bg-[rgba(255,255,255,0.06)]'
        }`}
        aria-label="Notifications"
      >
        <Bell size={18} className={unreadCount > 0 ? 'animate-[wiggle_1s_ease-in-out]' : ''} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-0.5 text-[9px] font-bold text-white bg-[#FF4D4D] rounded-full leading-none animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute right-0 top-11 w-[380px] max-h-[500px] flex flex-col bg-[#0D1B2A] border border-[rgba(22,194,255,0.12)] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(22,194,255,0.08)]">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-[#16C2FF]" />
              <span className="text-sm font-semibold text-[#E6F1FF]">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold text-[#16C2FF] bg-[rgba(22,194,255,0.12)] rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-[10px] text-[#93A4B8] hover:text-[#16C2FF] transition-colors"
                >
                  <Check size={11} />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-[#93A4B8] hover:text-[#E6F1FF] transition-colors"
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-5 h-5 border-2 border-[#16C2FF] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Bell size={28} className="text-[#2A3F55]" />
                <p className="text-sm text-[#4A6080]">No notifications yet</p>
                <p className="text-xs text-[#2A3F55]">Actions and updates will appear here</p>
              </div>
            ) : (
              <ul className="divide-y divide-[rgba(22,194,255,0.05)]">
                {notifications.map((notif) => {
                  const cfg = typeConfig[notif.notification_type] || defaultConfig;
                  const IconComp = cfg.icon;
                  return (
                    <li
                      key={notif.id}
                      onClick={() => !notif.is_read && markOneRead(notif.id)}
                      className={`flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-[rgba(255,255,255,0.03)] ${
                        !notif.is_read ? 'bg-[rgba(22,194,255,0.03)]' : ''
                      }`}
                    >
                      {/* Icon */}
                      <div className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg ${cfg.bg} border ${cfg.border}`}>
                        <IconComp size={14} className={cfg.color} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[10px] font-semibold uppercase tracking-wide ${cfg.color}`}>
                              {cfg.label}
                            </span>
                            {!notif.is_read && (
                              <span className="w-1.5 h-1.5 rounded-full bg-[#16C2FF] flex-shrink-0" />
                            )}
                          </div>
                          <span className="text-[10px] text-[#4A6080] flex-shrink-0 mt-0.5">
                            {formatTime(notif.created_at)}
                          </span>
                        </div>
                        <p className="text-xs font-medium text-[#C8D8E8] mt-0.5">{notif.title}</p>
                        <p className="text-[11px] text-[#6B8299] mt-0.5 line-clamp-2 leading-relaxed">{notif.message}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-[rgba(22,194,255,0.08)] flex items-center justify-between">
              <span className="text-[10px] text-[#4A6080]">{notifications.length} total notifications</span>
              <button
                onClick={fetchNotifications}
                className="text-[10px] text-[#93A4B8] hover:text-[#16C2FF] transition-colors"
              >
                Refresh
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
